import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  InputAdornment,
  Link,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import { listFinanceLedger } from '../../api/finance';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PAYMENT_STATUS_LABEL, PaymentStatus, PaymentType } from '../../types/payment';
import { FinanceFilters, FinanceLedgerRow, LedgerTotal } from '../../types/finance';
import { ApprovalStatusChip } from './ApprovalStatusChip';
import { PaymentReviewDialog } from './PaymentReviewDialog';
import { ExportButtons } from './ExportButtons';
import { FinanceFilterBar } from './FinanceFilterBar';
import { formatDate, formatMoney } from './financeFormat';
import { NumberLabel } from '../../components/NumberLabel';

const STATUSES: PaymentStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'REVERSED'];
const TYPES: PaymentType[] = ['PAYMENT', 'DISCOUNT', 'SCHOLARSHIP', 'REFUND'];
const totalsLine = (totals: LedgerTotal[], status: PaymentStatus) =>
  totals
    .filter((t) => t.status === status)
    .map(
      (t) =>
        `${t.type.toLowerCase()} ${formatMoney(t.totalWithTax, t.currency)}${t.lkrAmount ? ` (LKR ${t.lkrAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })})` : ''}`,
    )
    .join(' · ');

/** Finance → Payment Ledger (Phase 96): every payment, discount,
 * scholarship and refund with its own stored SSCL, exchange-rate and LKR
 * snapshot — nothing recalculated from today's rates. Totals are split by
 * approval status (Phase 97); only approved ones count towards anything. */
export const PaymentLedgerPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PaymentStatus | ''>('');
  const [type, setType] = useState<PaymentType | ''>('');
  const [filters, setFilters] = useState<FinanceFilters>({});
  // A row opens the payment's review dialog: details, history, and the
  // actions its status allows (reverse an approval, approve again…).
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  const params = {
    ...filters,
    branchId: selectedBranchId,
    search: search || undefined,
    status: status || undefined,
    type: type || undefined,
  };
  const listQuery = useQuery({
    queryKey: ['finance-ledger', paginationModel, params],
    queryFn: () =>
      listFinanceLedger({
        ...params,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef<FinanceLedgerRow>[] = [
    {
      field: 'paymentNumber',
      headerName: 'Payment',
      width: 190,
      renderCell: ({ row }) => <NumberLabel value={row.paymentNumber} kind="payment" />,
    },
    { field: 'paymentDate', headerName: 'Date', width: 110, valueFormatter: (v) => formatDate(v) },
    {
      field: 'student',
      headerName: 'Student',
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Link component={RouterLink} to={`/finance/students/${row.student.id}?paymentId=${row.id}`}>
          {row.student.name} ({row.student.studentNumber})
        </Link>
      ),
    },
    { field: 'branch', headerName: 'Branch', width: 110, valueFormatter: (v) => v ?? '—' },
    {
      field: 'category',
      headerName: 'Category',
      width: 180,
      valueGetter: (_v, row) => row.category.name,
    },
    { field: 'type', headerName: 'Type', width: 105 },
    {
      field: 'status',
      headerName: 'Approval status',
      width: 180,
      renderCell: ({ row }) => <ApprovalStatusChip status={row.status} />,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (_v, row) => formatMoney(row.amount, row.currency),
    },
    {
      field: 'taxAmount',
      headerName: 'SSCL',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number | null) => (v == null ? '—' : v.toFixed(2)),
    },
    {
      field: 'totalWithTax',
      headerName: 'Total incl. SSCL',
      width: 135,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (_v, row) => formatMoney(row.totalWithTax, row.currency),
    },
    {
      field: 'exchangeRate',
      headerName: 'Rate',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number | null) => (v == null ? '—' : v.toFixed(2)),
    },
    {
      field: 'lkrAmount',
      headerName: 'LKR',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number | null) =>
        v == null ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
    {
      field: 'receipt',
      headerName: 'Receipt',
      width: 105,
      valueGetter: (_v, row) => row.receipt?.receiptNumber ?? '—',
    },
    { field: 'recordedBy', headerName: 'Recorded by', width: 130, valueFormatter: (v) => v ?? '—' },
    { field: 'approvedBy', headerName: 'Reviewed by', width: 130, valueFormatter: (v) => v ?? '—' },
    {
      field: 'approvedAt',
      headerName: 'Reviewed',
      width: 110,
      valueFormatter: (v) => (v ? formatDate(v) : '—'),
    },
    {
      field: 'rejectionReason',
      headerName: 'Rejection reason',
      width: 200,
      valueFormatter: (v) => v ?? '—',
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={ReceiptLongIcon}
        color="#5B2DA3"
        title="Payment Ledger"
        subtitle="All payment transactions with their recorded SSCL and exchange-rate snapshots."
        actions={<ExportButtons params={{ report: 'ledger', ...params }} />}
      />
      <FinanceFilterBar
        value={filters}
        onChange={(next) => {
          setFilters(next);
          resetPage();
        }}
      >
        <TextField
          placeholder="Search payment, reference, student…"
          size="small"
          sx={{ width: 250 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ minWidth: 200 }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as PaymentStatus | '');
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {PAYMENT_STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Type"
          sx={{ minWidth: 130 }}
          value={type}
          onChange={(e) => {
            setType(e.target.value as PaymentType | '');
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          {TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t.toLowerCase()}
            </MenuItem>
          ))}
        </TextField>
      </FinanceFilterBar>

      {listQuery.data && listQuery.data.totals.length > 0 && (
        <Stack spacing={0.25} sx={{ mb: 1 }} data-testid="ledger-totals">
          {STATUSES.filter((st) => listQuery.data.totals.some((t) => t.status === st)).map((st) => (
            <Typography key={st} variant="body2">
              {st === 'APPROVED'
                ? 'Approved (counted)'
                : st === 'PENDING'
                  ? 'Pending finance approval (not counted)'
                  : 'Rejected (not counted)'}
              : <strong>{totalsLine(listQuery.data.totals, st)}</strong>
            </Typography>
          ))}
        </Stack>
      )}

      {listQuery.isError ? (
        <Alert severity="error">Couldn't load the ledger.</Alert>
      ) : (
        <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[20, 50, 100]}
            slots={{ toolbar: TableToolbar }}
            localeText={{ noRowsLabel: 'No payments match these filters.' }}
            disableRowSelectionOnClick
            onRowClick={(p) => setReviewId(p.row.id)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}
      <PaymentReviewDialog
        paymentId={reviewId}
        onClose={() => setReviewId(null)}
        onDecided={(payment, decision) => {
          setToast(
            decision === 'reversed'
              ? `${payment.paymentNumber} reversed — its receipt is void.`
              : decision === 'approved'
                ? `${payment.paymentNumber} approved${payment.receipt ? ` — receipt ${payment.receipt.receiptNumber} issued` : ''}.`
                : `${payment.paymentNumber} rejected.`,
          );
          void listQuery.refetch();
        }}
      />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity="success" onClose={() => setToast(null)}>
            {toast}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
